// Modern Livestream Design System - Inspired by Court Management
// This creates a consistent, clean, and modern look across the app

export const livestreamDesignSystem = {
  // Layout Components
  pageContainer: {
    base: "min-h-screen bg-gradient-to-br from-primary-bg via-primary-bg to-secondary-bg",
    content: "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
  },

  // Header Components
  header: {
    container: "bg-white shadow-medium border-b border-border sticky top-0 z-50",
    content: "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6",
    titleSection: "flex items-center gap-4 mb-4",
    iconContainer: "w-12 h-12 bg-teal rounded-lg flex items-center justify-center shadow-small",
    icon: "text-2xl",
    title: "text-2xl sm:text-3xl font-bold bg-gradient-to-r from-teal to-teal bg-clip-text text-transparent font-primary",
    subtitle: "text-sm sm:text-base text-text-secondary mt-1 font-secondary"
  },

  // Card Components - Compact and Clean
  card: {
    container: "bg-white rounded-lg shadow-small border border-border transition-all duration-300 hover:shadow-medium hover:-translate-y-0.5 hover:border-teal",
    content: "p-3 sm:p-4",
    header: "flex justify-between items-start mb-3",
    title: "text-base sm:text-lg font-semibold text-title font-primary line-clamp-2",
    subtitle: "text-xs sm:text-sm text-text-secondary font-secondary",
    body: "space-y-2 mb-3",
    footer: "flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-border/50"
  },

  // Status Components - Compact with Brand Colors
  status: {
    badge: "inline-flex items-center px-2 py-1 rounded-md text-xs font-semibold uppercase tracking-wide",
    live: "bg-success text-white",
    scheduled: "bg-orange text-white",
    completed: "bg-teal text-white",
    cancelled: "bg-error text-white",
    offline: "bg-grey text-white"
  },

  // Button Components - Compact with Icons
  button: {
    base: "inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-md font-medium text-xs transition-all duration-300 font-secondary",
    primary: "bg-teal text-white shadow-small hover:bg-teal/90 hover:shadow-medium hover:-translate-y-0.5",
    secondary: "bg-transparent text-teal border border-teal hover:bg-teal/10 hover:border-teal/80",
    outline: "bg-transparent text-text-primary border border-border hover:bg-hover hover:border-teal",
    danger: "bg-error text-white hover:bg-error/90",
    success: "bg-success text-white hover:bg-success/90",
    iconOnly: "w-8 h-8 p-0 rounded-md",
    compact: "px-2 py-1 text-xs",
    small: "px-2.5 py-1.5 text-xs",
    large: "px-4 py-2.5 text-sm"
  },

  // Form Components
  form: {
    container: "space-y-4",
    group: "space-y-2",
    label: "text-sm font-medium text-text-primary font-primary",
    input: "w-full px-4 py-2 bg-input-bg border border-border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:border-accent-teal focus:ring-2 focus:ring-accent-teal/20 transition-all duration-300",
    select: "w-full px-4 py-2 bg-input-bg border border-border rounded-lg text-text-primary cursor-pointer focus:outline-none focus:border-accent-teal focus:ring-2 focus:ring-accent-teal/20 transition-all duration-300",
    textarea: "w-full px-4 py-2 bg-input-bg border border-border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:border-accent-teal focus:ring-2 focus:ring-accent-teal/20 transition-all duration-300 resize-vertical min-h-[100px]"
  },

  // Grid Components
  grid: {
    container: "grid gap-4 sm:gap-6",
    responsive: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4",
    twoColumn: "grid-cols-1 lg:grid-cols-2",
    threeColumn: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
  },

  // Navigation Components
  nav: {
    container: "flex flex-wrap gap-2 sm:gap-4 mb-6",
    tab: "px-4 py-2 rounded-lg font-medium text-sm transition-all duration-300 font-secondary",
    tabActive: "bg-accent-teal text-white shadow-small",
    tabInactive: "bg-transparent text-text-secondary border border-border hover:bg-accent-teal/10 hover:text-accent-teal hover:border-accent-teal"
  },

  // Filter Components
  filter: {
    container: "flex flex-col sm:flex-row gap-4 p-4 sm:p-6 bg-white rounded-xl shadow-small border border-border",
    input: "flex-1 px-4 py-2 bg-input-bg border border-border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:border-accent-teal focus:ring-2 focus:ring-accent-teal/20 transition-all duration-300",
    select: "px-4 py-2 bg-input-bg border border-border rounded-lg text-text-primary cursor-pointer focus:outline-none focus:border-accent-teal focus:ring-2 focus:ring-accent-teal/20 transition-all duration-300",
    button: "px-4 py-2 bg-transparent text-text-secondary border border-border rounded-lg hover:bg-accent-teal/10 hover:text-accent-teal hover:border-accent-teal transition-all duration-300"
  },

  // Loading Components
  loading: {
    container: "flex justify-center items-center min-h-[400px]",
    spinner: "w-8 h-8 sm:w-10 sm:h-10 border-4 border-accent-teal border-t-transparent rounded-full animate-spin",
    text: "text-text-secondary font-secondary mt-4"
  },

  // Error Components
  error: {
    container: "text-center py-8 sm:py-12",
    icon: "text-4xl sm:text-5xl mb-4 opacity-50",
    title: "text-lg sm:text-xl font-bold text-text-primary mb-2 font-primary",
    message: "text-sm sm:text-base text-text-secondary mb-4 font-secondary",
    button: "px-4 py-2 bg-accent-teal text-white rounded-lg hover:bg-accent-teal/90 transition-all duration-300"
  },

  // Empty State Components
  empty: {
    container: "text-center py-8 sm:py-12",
    icon: "text-4xl sm:text-5xl mb-4 opacity-50",
    title: "text-lg sm:text-xl font-bold text-text-primary mb-2 font-primary",
    message: "text-sm sm:text-base text-text-secondary mb-4 font-secondary",
    button: "px-4 py-2 bg-accent-teal text-white rounded-lg hover:bg-accent-teal/90 transition-all duration-300"
  },

  // Modal Components
  modal: {
    overlay: "fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4",
    container: "bg-white rounded-xl shadow-large max-w-2xl w-full max-h-[90vh] overflow-y-auto",
    header: "flex justify-between items-center p-6 border-b border-border",
    title: "text-xl font-bold text-title font-primary",
    close: "w-8 h-8 bg-transparent text-text-secondary border-none rounded-lg cursor-pointer transition-all duration-300 hover:bg-hover hover:text-text-primary flex items-center justify-center",
    content: "p-6",
    footer: "flex gap-2 justify-end p-6 border-t border-border"
  },

  // Player Components
  player: {
    container: "relative w-full bg-dark-bg rounded-xl overflow-hidden shadow-large aspect-video",
    controls: "absolute bottom-4 left-4 right-4 flex justify-between items-center bg-black/50 backdrop-blur-sm rounded-lg p-3",
    button: "w-10 h-10 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center text-white transition-all duration-300"
  },

  // Stats Components
  stats: {
    container: "flex items-center gap-2 text-sm text-text-secondary",
    icon: "w-4 h-4 text-accent-teal",
    text: "font-secondary"
  },

  // Price Components
  price: {
    container: "text-xs sm:text-sm font-secondary",
    free: "text-teal font-semibold",
    paid: "text-orange font-semibold",
    required: "text-orange font-semibold"
  },

  // Icon Components
  icon: {
    container: "w-4 h-4 flex items-center justify-center",
    small: "w-3 h-3",
    medium: "w-4 h-4",
    large: "w-5 h-5",
    teal: "text-teal",
    orange: "text-orange",
    grey: "text-grey",
    success: "text-success",
    error: "text-error"
  }
};

// Utility function to combine classes
export const combineClasses = (...classes) => {
  return classes.filter(Boolean).join(' ');
};

// Component-specific class combinations
export const componentClasses = {
  // LivestreamManagement
  managementPage: combineClasses(
    livestreamDesignSystem.pageContainer.base
  ),
  managementHeader: combineClasses(
    livestreamDesignSystem.header.container
  ),
  managementHeaderContent: combineClasses(
    livestreamDesignSystem.header.content
  ),
  managementTitleSection: combineClasses(
    livestreamDesignSystem.header.titleSection
  ),
  managementIconContainer: combineClasses(
    livestreamDesignSystem.header.iconContainer
  ),
  managementTitle: combineClasses(
    livestreamDesignSystem.header.title
  ),
  managementSubtitle: combineClasses(
    livestreamDesignSystem.header.subtitle
  ),

  // LivestreamCard
  livestreamCard: combineClasses(
    livestreamDesignSystem.card.container
  ),
  livestreamCardContent: combineClasses(
    livestreamDesignSystem.card.content
  ),
  livestreamCardHeader: combineClasses(
    livestreamDesignSystem.card.header
  ),
  livestreamCardTitle: combineClasses(
    livestreamDesignSystem.card.title
  ),
  livestreamCardSubtitle: combineClasses(
    livestreamDesignSystem.card.subtitle
  ),
  livestreamCardBody: combineClasses(
    livestreamDesignSystem.card.body
  ),
  livestreamCardFooter: combineClasses(
    livestreamDesignSystem.card.footer
  ),

  // Status Badge
  statusBadge: (status) => combineClasses(
    livestreamDesignSystem.status.badge,
    status === 'IN_PROGRESS' ? livestreamDesignSystem.status.live :
    status === 'SCHEDULED' ? livestreamDesignSystem.status.scheduled :
    status === 'COMPLETED' ? livestreamDesignSystem.status.completed :
    status === 'CANCELLED' ? livestreamDesignSystem.status.cancelled :
    livestreamDesignSystem.status.offline
  ),

  // Buttons - Compact with Icons
  buttonPrimary: combineClasses(
    livestreamDesignSystem.button.base,
    livestreamDesignSystem.button.primary
  ),
  buttonSecondary: combineClasses(
    livestreamDesignSystem.button.base,
    livestreamDesignSystem.button.secondary
  ),
  buttonOutline: combineClasses(
    livestreamDesignSystem.button.base,
    livestreamDesignSystem.button.outline
  ),
  buttonDanger: combineClasses(
    livestreamDesignSystem.button.base,
    livestreamDesignSystem.button.danger
  ),
  buttonIconOnly: combineClasses(
    livestreamDesignSystem.button.base,
    livestreamDesignSystem.button.iconOnly
  ),
  buttonCompact: combineClasses(
    livestreamDesignSystem.button.base,
    livestreamDesignSystem.button.compact
  ),
  buttonSmall: combineClasses(
    livestreamDesignSystem.button.base,
    livestreamDesignSystem.button.small
  ),

  // Grid
  livestreamGrid: combineClasses(
    livestreamDesignSystem.grid.container,
    livestreamDesignSystem.grid.responsive
  ),

  // Navigation
  navContainer: combineClasses(
    livestreamDesignSystem.nav.container
  ),
  navTab: (isActive) => combineClasses(
    livestreamDesignSystem.nav.tab,
    isActive ? livestreamDesignSystem.nav.tabActive : livestreamDesignSystem.nav.tabInactive
  ),

  // Filters
  filterContainer: combineClasses(
    livestreamDesignSystem.filter.container
  ),
  filterInput: combineClasses(
    livestreamDesignSystem.filter.input
  ),
  filterSelect: combineClasses(
    livestreamDesignSystem.filter.select
  ),
  filterButton: combineClasses(
    livestreamDesignSystem.filter.button
  ),

  // Loading
  loadingContainer: combineClasses(
    livestreamDesignSystem.loading.container
  ),
  loadingSpinner: combineClasses(
    livestreamDesignSystem.loading.spinner
  ),

  // Error
  errorContainer: combineClasses(
    livestreamDesignSystem.error.container
  ),
  errorButton: combineClasses(
    livestreamDesignSystem.error.button
  ),

  // Empty State
  emptyContainer: combineClasses(
    livestreamDesignSystem.empty.container
  ),
  emptyButton: combineClasses(
    livestreamDesignSystem.empty.button
  )
};
